import React, { FC, useState } from 'react';
import { DialogContent, Box, Grid, TextField, DialogActions, Button, FormControl, InputAdornment } from '@mui/material';
import { MaterialBatch, LabMaterial } from 'models/material';
import { ApolloCache, Reference, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { createMaterialTemplate } from 'apollo/templates/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { getTime } from 'date-fns';
import MaterialBatchManager from '../material-batch-manager.component.tsx/material-batch-manager.component';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';

type AddMaterialFormProps = {
    onAddMaterial: (material: LabMaterial) => void;
    onCloseDialog: () => void;
};
const AddMaterialForm: FC<AddMaterialFormProps> = ({ onAddMaterial, onCloseDialog }: AddMaterialFormProps) => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'workflow', 'orders']);
    const [batches, setBatches] = useState<MaterialBatch[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    const [createMaterial] = useMutation(createMaterialTemplate, {
        onCompleted: ({ createMaterial }) => {
            onAddMaterial(createMaterial);
        },
        onError(error) {
            showBoundary(error);
        },
        update(cache: ApolloCache<LabMaterial>, { data: { createMaterial } }) {
            cache.modify({
                fields: {
                    labMaterials(materialRefs: Reference | (Reference | undefined)[], { toReference }) {
                        if (materialRefs instanceof Array) {
                            return [...materialRefs, toReference(createMaterial)];
                        }
                        return [materialRefs, toReference(createMaterial)];
                    },
                },
            });
        },
    });

    const validate = (values: LabMaterial) => {
        const errors: Record<string, string> = {};
        if (!values.materialCode) {
            errors.materialCode = translate('actions-materials:VALIDATION:MATERIAL_CODE');
        }
        if (!values.description) {
            errors.description = translate('workflow:VALIDATION:DESCRIPTION');
        }
        return errors;
    };

    const batchesVars = (batches: MaterialBatch[]) => {
        const batchesVars: any = [];
        batches.forEach((batch) => {
            batchesVars.push({
                batchNumber: batch.batchNumber,
                quantity: batch.fullBatchQuantity, // Change to batch.quantity for next step of batch management
                fullBatchQuantity: batch.fullBatchQuantity,
                createdOn: getTime(batch.createdOn),
                closed: batch.closed,
                closedDate: batch.closedDate ? getTime(batch.closedDate) : null,
            });
        });
        return batchesVars;
    };

    const formik = useFormik({
        initialValues: {
            materialCode: '',
            description: '',
            vat: 0,
            batchInventoryManagement: false,
        },
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const vat = parseFloat(values.vat?.toString() || '');
            await createMaterial({ variables: { ...values, vat: vat || 0, batches: batchesVars(batches) } });
            onCloseDialog();
        },
    });

    const handleBatches = (batches: MaterialBatch[]) => {
        setBatches(batches);
    };
    const handleUseInventory = (useInventory: boolean) => {
        formik.setFieldValue('batchInventoryManagement', useInventory);
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Box mt={1} mb={4}>
                    <Grid container spacing={2}>
                        <Grid item md={4} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="material-code"
                                    margin="dense"
                                    label={translate('actions-materials:CODE')}
                                    required
                                    helperText={formik.touched.materialCode && formik.errors.materialCode}
                                    error={formik.touched.materialCode && Boolean(formik.errors.materialCode)}
                                    {...formik.getFieldProps('materialCode')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={6} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    id="description"
                                    margin="dense"
                                    label={translate('common:DESCRIPTION')}
                                    required
                                    helperText={formik.touched.description && formik.errors.description}
                                    error={formik.touched.description && Boolean(formik.errors.description)}
                                    {...formik.getFieldProps('description')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={2} sm={12}>
                            <FormControl fullWidth>
                                <NumberOnlyTextField
                                    id="vat"
                                    label={translate('orders:VAT')}
                                    {...formik.getFieldProps('vat')}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
                <MaterialBatchManager onBatches={handleBatches} onUseInventory={handleUseInventory} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseDialog}>{translate('common:CANCEL')}</Button>
                <Button variant="contained" disabled={!formik.isValid} type="submit">
                    {translate('lab-settings:SAVE_MATERIAL')}
                </Button>
            </DialogActions>
        </form>
    );
};

export default AddMaterialForm;
